// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. The original lives
// in @roc/feature-borrowers and is left untouched so other portals keep the old
// design. MobX stores/services remain shared (imported from @roc/feature-borrowers).

import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { DialogState, StandardDialog } from '@roc/ui';
import { TrackRecordPropertyForm } from './trackRecordPropertyForm';
import { TrackRecordProperties } from './trackRecordProperties';
import { useEffect, useState } from 'react';
import { useBorrowerBaseballCardStore } from '@roc/feature-borrowers';
import { TrackRecordExtractionDialog } from './trackRecordExtractionDialog';
import { useDownloadTrackRecordTemplate } from '../hooks/useDownloadTrackRecordTemplate';
import { BorrowerTrackRecordVerificationStore } from '@roc/feature-borrowers';

interface TrackRecordFirstStepProps {
  store: BorrowerTrackRecordVerificationStore;
  onDocUploadCallback?: () => void;
  hideTable?: boolean;
}

export const TrackRecordFirstStep = observer(({ store, onDocUploadCallback = () => { }, hideTable = false }: TrackRecordFirstStepProps) => {

  // Both portals provide borrowerBaseballCardStore through the same StoreContext,
  // so a single unconditional hook call works for the internal and public portal.
  const { borrowerBaseballCardStore } = useBorrowerBaseballCardStore();
  const handleDownloadTemplate = useDownloadTrackRecordTemplate();
  const [showExtractionDialog, setShowExtractionDialog] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');

  // Read extraction state directly from the store (single source of truth).
  // Destructure here so MobX tracks these observables during render and triggers re-renders.
  const { extractionFailed, extractionComplete } = store;

  useEffect(() => {
    if (store.isPropertyFormActive) {
      borrowerBaseballCardStore.propertyFormStore.resetForm();
      borrowerBaseballCardStore.propertyInformationDialogState = DialogState.ADD;
    }
  }, [store.isPropertyFormActive]);

  const handleCloseModal = async () => {
    try {
      await store.fetchTrackRecordProperties();
    } catch (e) {
      // ignore fetch error on modal close
    } finally {
      await borrowerBaseballCardStore.setPropagateAddress(false);
      await store.setPropertyFormActive(false);
      store.trackRecordPropertyFormStore.reset();
      borrowerBaseballCardStore.propertyFormStore.resetForm();
    }
  };

  const handleContinueUpload = (fileName: string) => {
    setUploadedFileName(fileName);
    store.setPropertyFormActive(false);
    store.resetExtractionState();
    setShowExtractionDialog(true);
    store.invokeAgent({
      // loanId lets the backend resolve the uploaded Track Record doc's S3 path.
      loanId: borrowerBaseballCardStore.loanId,
      trackRecordExcel: fileName,
    });
  };

  // Invoked on the Search click in the entity look-up popup with the entity name(s)
  // the user searched. The agent does its own look-up; we just pass the search input.
  const handleEntityInvokeAgent = (entityNames: string[]) => {
    store.setPropertyFormActive(false);
    store.resetExtractionState();
    store.setAlreadySavedRecords([]);
    setShowExtractionDialog(true);
    store.invokeAgent({ entityNames });
  };

  // Invoked on the Search click in the property look-up popup with the address
  // (custom_transactions shape) the user searched.
  const handlePropertyInvokeAgent = (customTransactions: any[]) => {
    store.setPropertyFormActive(false);
    store.resetExtractionState();
    store.setAlreadySavedRecords([]);
    setShowExtractionDialog(true);
    const dealTypeStore = store.trackRecordPropertyFormStore;
    store.invokeAgent({
      customTransactions,
      // Deal Type checkboxes from the property form (Fix & Flip -> bridge, Rental -> term, Ground Up -> ground_up)
      bridge: dealTypeStore?.bridgeSelected,
      groundUp: dealTypeStore?.groundUpSelected,
      term: dealTypeStore?.termSelected,
      // Property search (look-up) -> results come from public records / MLS
      recordSourceType: 'MLS Data',
    });
  };

  // Manual add ("property not listed -> add it now")
  const handleManualAddInvokeAgent = (data: any) => {
    store.invokeAgent({
      manualProperty: data?.manualProperty,
      bridge: data?.bridge,
      groundUp: data?.groundUp,
      term: data?.term,
      recordSourceType: 'User Inputted',
    });
  };

  const handleExtractionClose = async () => {
    setShowExtractionDialog(false);
    store.resetExtractionState();
    await handleCloseModal();
  };

  const handleExtractionCancel = async () => {
    store.cancelExtraction();
    setShowExtractionDialog(false);
    store.resetExtractionState();
    await handleCloseModal();
  };

  const handleTryAgain = async () => {
    setShowExtractionDialog(false);
    store.resetExtractionState();
    await handleCloseModal();
  };

  const handleSaveAndFinish = async () => {
    await store.saveExtractionResults();
    setShowExtractionDialog(false);
    store.resetExtractionState();
    await handleCloseModal();
  };

  const handleUploadAdditionalDocument = () => {
    setShowExtractionDialog(false);
    store.resetExtractionState();
    store.trackRecordPropertyFormStore.onFieldChange('screenType', 'upload');
    store.setPropertyFormActive(true);
  };

  const handleLookUpByEntity = async () => {
    setShowExtractionDialog(false);
    store.resetExtractionState();
    store.trackRecordPropertyFormStore.onFieldChange('screenType', 'entityName');
    store.setPropertyFormActive(true);
  };

  const handleLookUpByProperty = async () => {
    setShowExtractionDialog(false);
    store.resetExtractionState();
    store.trackRecordPropertyFormStore.onFieldChange('screenType', 'propertyAddress');
    store.setPropertyFormActive(true);
  };

  useEffect(() => {
    return () => {
      store.cancelExtraction();
    };
  }, []);

  const borrowerName = [store.borrowerInfo?.firstName, store.borrowerInfo?.lastName]
    .filter(Boolean)
    .join(' ') || store.borrowerInfo?.borrowerName || '';

  return (
    <>
      {!hideTable && (
        <Grid item xs={12}>
          <TrackRecordProperties store={store} borrowerBaseballCardStore={borrowerBaseballCardStore} />
        </Grid>
      )}
      <StandardDialog open={store.isPropertyFormActive} handleClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        removeContentBox
        PaperProps={{
          style: {
            maxHeight: '90vh',
            overflowY: 'auto',
          }
        }}
        dialogContent={
          <Grid item xs={12}>
            <TrackRecordPropertyForm
              store={store}
              handleClose={handleCloseModal}
              onContinue={handleContinueUpload}
              borrowerBaseballCardStore={borrowerBaseballCardStore}
              onEntityInvokeAgent={handleEntityInvokeAgent}
              onPropertyInvokeAgent={handlePropertyInvokeAgent}
              onManualAddInvokeAgent={handleManualAddInvokeAgent}
            />
          </Grid>
        }
      >
      </StandardDialog>
      <TrackRecordExtractionDialog
        open={showExtractionDialog}
        fileName={uploadedFileName}
        borrowerName={borrowerName}
        hasError={showExtractionDialog && extractionFailed}
        extractionSuccess={showExtractionDialog && extractionComplete}
        isLoading={store.loading}
        traceSteps={store.extractionTraceSteps}
        extractionResults={store.extractionResults}
        onClose={handleExtractionClose}
        onCancel={handleExtractionCancel}
        onTryAgain={handleTryAgain}
        onDownloadTemplate={handleDownloadTemplate}
        onLookUpByEntity={handleLookUpByEntity}
        onLookUpByProperty={handleLookUpByProperty}
        onSaveAndFinish={handleSaveAndFinish}
        onUploadAdditionalDocument={handleUploadAdditionalDocument}
        onRemoveProperty={(i) => runInAction(() => { store.extractionResults.splice(i, 1); })}
      />
    </>
  );
});
